 

 
import React, { useEffect, useMemo, useRef, useState } from "react";
import { ScanBarcode, Printer, PlusSquare, Search } from "lucide-react";
import { motion } from "framer-motion";
import axios from "axios";
import { BrowserMultiFormatReader } from "@zxing/library";
import config from "../../config/config";



const PAGE_SIZE = 50;

export default function BarcodeManager() {
  // ======================
  // STATE
  // ======================
  const [books, setBooks] = useState([]);
  const [tab, setTab] = useState("generate");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);

  const [selected, setSelected] = useState({});
  const [labels, setLabels] = useState([]);
  const [pageSize, setPageSize] = useState("A4");
  const [printQty, setPrintQty] = useState({});

  const [cameraActive, setCameraActive] = useState(false);
  const [scannedCode, setScannedCode] = useState("");

  const videoRef = useRef(null); // video element
  const codeReader = useRef(null); // ZXing reader
  const { API_BASE_URL } =config;
  console.log(API_BASE_URL);

  // ======================
  // FETCH BOOKS
  // ======================
  const fetchBooks = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API_BASE_URL}/Home/Getbooks`, {}, {
        headers: { "Content-Type": "application/json" }
      });
      setBooks(res.data || []);
    } catch {
      alert("Failed to load books");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBooks();
  }, []);

  // ======================
  // FILTER + PAGINATION
  // ======================
  const filtered = useMemo(() => {
    let data = [...books];
    data = tab === "generate"
      ? data.filter(b => !b.isBarcodeGenerated)
      : data.filter(b => b.isBarcodeGenerated);
    if (search) data = data.filter(b =>
      (b.booknameinenglish || b.product)?.toLowerCase().includes(search.toLowerCase())
    );
    return data;
  }, [books, tab, search]);

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  // ======================
  // SELECTION HANDLERS
  // ======================
  const toggleSelect = (book) => {
    setSelected(prev => {
      const copy = { ...prev };
      if (copy[book.id]) delete copy[book.id];
      else copy[book.id] = { ...book, qty: 1 };
      return copy;
    });
  };

  const clearSelection = () => {
    if (!Object.keys(selected).length) return;
    if (window.confirm("Clear all selected books?")) setSelected({});
  };

  // ======================
  // GENERATE BARCODE
  // ======================
  const handleGenerate = async () => {
    const ids = Object.keys(selected).map(id => parseInt(id));
    if (!ids.length) return alert("Select books first");

    try {
      await axios.post(`${API_BASE_URL}/BarCode/GenerateBarcodeForBook`, ids, {
        headers: { "Content-Type": "application/json" }
      });
      alert("✅ Barcodes generated");
      setSelected({});
      setTab("print");
      fetchBooks();
    } catch (err) {
      console.error(err.response?.data);
      alert("❌ Generation failed");
    }
  };

  // ======================
  // PRINT QUEUE
  // ======================
  // const addToPrint = (book) => {
  //   console.log("Adding to print:", book);
  //   setLabels(prev => {
  //     const found = prev.find(l => l.id === book.id);
  //     if (found) return prev.map(l => l.id === book.id ? { ...l, qty: l.qty + 1 } : l);
  //     return [...prev, { ...book, qty: 1 }];
  //   });
  // };

  const addToPrint = (book, newQty) => {
    setLabels(prev => {
      const found = prev.find(l => l.id === book.id);

      // If the new quantity is 0 or less, remove the book from the queue
      if (newQty <= 0) {
        return prev.filter(l => l.id !== book.id);
      }

      if (found) {
        // SET the quantity to exactly what was passed in the parameter
        return prev.map(l =>
          l.id === book.id ? { ...l, qty: newQty } : l
        );
      }

      // If not found, add it with the specified quantity
      return [...prev, { ...book, qty: newQty }];
    });
  };

  const updateLabelQty = (id, qty) => {
    setLabels(prev => prev.map(l => l.id === id ? { ...l, qty: Math.max(1, parseInt(qty) || 1) } : l));
  };

  const removeLabel = (id) => {
    setLabels(prev => prev.filter(l => l.id !== id));
  };

  // ======================
  // PRINT
  // ======================
  // const handlePrint = () => {
  //   window.print();
  // };

  const handlePrint = () => {
    const images = document.querySelectorAll("img");

    Promise.all(
      [...images].map(img => {
        if (img.complete) return Promise.resolve();
        return new Promise(res => img.onload = res);
      })
    ).then(() => window.print());
  };



  // ======================
  // CAMERA SCANNER
  // ======================
  const startScan = async () => {
    if (cameraActive) {
      // STOP scanning if already active
      codeReader.current?.reset();
      videoRef.current.srcObject?.getTracks().forEach(t => t.stop());
      setCameraActive(false);
      return;
    }

    // INIT ZXing code reader
    codeReader.current = new BrowserMultiFormatReader();

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "environment" } // rear camera
      });

      videoRef.current.srcObject = stream;
      videoRef.current.play();
      setCameraActive(true);

      // REAL-TIME barcode scanning
      codeReader.current.decodeFromVideoDevice(
        null, // default device
        videoRef.current,
        (result, err) => {
          if (result) {
            const code = result.getText();
            setScannedCode(code);
            console.log("Scanned barcode:", code);

            // Auto-select matching book
            const book = books.find(b => b.barcodeValue === code);
            if (book) setSelected(prev => ({ ...prev, [book.id]: { ...book, qty: 1 } }));

            // AUTO-CLOSE camera after successful scan
            codeReader.current?.reset();
            videoRef.current.srcObject?.getTracks().forEach(t => t.stop());
            setCameraActive(false);
          }
        }
      );
    } catch {
      alert("Camera access denied or not available");
    }
  };

  // ======================
  // RENDER PRINT LABELS
  // ======================
  const renderLabels = () => {
    const result = [];
    labels.forEach(l => {
      for (let i = 0; i < l.qty; i++) result.push({ ...l, key: `${l.id}-${i}` });
      
    });
    return result;

  };

  // ======================
  // DASHBOARD STATS
  // ======================
  const totalBooks = books.length;
  const barcodeGenerated = books.filter(b => b.isBarcodeGenerated).length;
  const remainingBarcodes = totalBooks - barcodeGenerated;

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-purple-50">
      {/* ======================
          DASHBOARD CARDS
      ====================== */}
      <div className="p-4 max-w-6xl mx-auto flex flex-col sm:flex-row justify-between gap-4 mb-4">
        <div className="bg-white p-4 rounded-2xl shadow-md text-center flex-1">
          <p className="font-bold text-gray-500">Total Books</p>
          <p className="text-2xl font-black">{totalBooks}</p>
        </div>
        <div className="bg-white p-4 rounded-2xl shadow-md text-center flex-1">
          <p className="font-bold text-gray-500">Barcodes Generated</p>
          <p className="text-2xl font-black">{barcodeGenerated}</p>
        </div>
        <div className="bg-white p-4 rounded-2xl shadow-md text-center flex-1">
          <p className="font-bold text-gray-500">Remaining</p>
          <p className="text-2xl font-black">{remainingBarcodes}</p>
        </div>
      </div>

      {/* ======================
          TOP BAR: SEARCH + SCAN + TAB
      ====================== */}
      <div className="sticky top-0 bg-white/95 backdrop-blur-xl shadow-md border-b p-4 space-y-2">
        <div className="flex flex-row gap-3 items-center">
          {/* SEARCH INPUT */}
          <div className="relative flex-1">
            <Search className="absolute top-1/2 -translate-y-1/2 left-3 text-gray-400" size={20} />
            <input
              placeholder="Search books..."
              className="w-full pl-10 sm:pl-14 p-3 sm:p-4-12 p-3 sm:p-4 rounded-2xl border-2 focus:border-indigo-500 focus:outline-none text-sm sm:text-base"
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            />
          </div>

          {/* SCAN BUTTON */}
          {/* <motion.button
            whileTap={{ scale: 0.95 }}
            onClick={startScan}
            className={`inline sm:w-auto px-4 sm:px-6 py-3 sm:py-4 rounded-2xl font-bold flex items-center justify-center gap-2 text-sm sm:text-base ${cameraActive ? "bg-red-600 text-white" : "bg-indigo-600 text-white"}`}
          >
            <ScanBarcode size={18} /> {cameraActive ? "Stop" : "Scan"}
          </motion.button> */}
        </div>

        {/* {scannedCode && <p className="text-green-700 font-bold mt-2 text-sm sm:text-base">Scanned: {scannedCode}</p>} */}

        {/* TAB SWITCH */}
        <div className="flex flex-row gap-2 mt-2">
          {["generate", "print"].map(t => (
            <button
              key={t}
              onClick={() => { setTab(t); setPage(1); }}
              className={`flex-1 py-2 sm:py-3 rounded-xl font-bold text-center text-sm sm:text-base transition-colors ${tab === t ? "bg-indigo-600 text-white" : "bg-gray-200"}`}
            >
              {t === "generate" ? "Generate" : "Print"}
            </button>
          ))}
        </div>
      </div>

      {/* ======================
          BOOK CARDS
      ====================== */}
      <div className="p-4 max-w-6xl mx-auto">
        {loading && <p className="text-center py-10 text-gray-500">Loading...</p>}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {paginated.map(book => (
            <motion.div
              key={book.id}
              whileHover={{ scale: 1.02 }}
              className="bg-white rounded-2xl shadow-lg p-4 sm:p-5 flex flex-col gap-2 sm:gap-3"
            >
              <h3 className="text-base sm:text-lg font-black truncate">{book.booknameinenglish || book.product}</h3>
              <p className="text-gray-600 text-sm sm:text-base">{book.price ? `₹${book.price}` : "Price N/A"}</p>
              {/* {book.barcodeValue && (
                <div className="mt-1 font-mono text-xs sm:text-sm bg-green-100 text-green-800 px-2 py-1 rounded-full inline-block truncate">{book.barcodeValue}</div>
              )} */}
              {book.barcodeImagePath && (
                <div className="mt-2 flex flex-col items-start gap-1">

                  {/* Barcode image */}
                  <img
                    src={`${API_BASE_URL}${book.barcodeImagePath}`}
                    alt="barcode"
                    className="h-20 object-contain"
                    onError={(e) => {
                      e.target.style.display = "none";
                    }}
                  />

                  {/* Barcode text (keep this – very useful) */}
                  <span className="font-mono text-xs text-gray-600 px-4 py-0.5 rounded-full">
                    {book.barcodeValue}
                  </span>

                </div>
              )}


              {/* {tab === "generate" && (
                <button
                  onClick={() => toggleSelect(book)}
                  className={`mt-2 py-2 sm:py-3 rounded-xl font-bold flex items-center justify-center gap-2 text-sm sm:text-base ${selected[book.id] ? "bg-indigo-600 text-white" : "bg-indigo-100"}`}
                >
                  <PlusSquare size={18} /> {selected[book.id] ? "Selected" : "Select"}
                </button>
              )} */}

              {tab === "generate" && (
                <label className="mt-3 flex items-center gap-3 cursor-pointer">

                  <input
                    type="checkbox"
                    checked={!!selected[book.id]}
                    onChange={() => toggleSelect(book)}
                    className="w-5 h-5 accent-indigo-600 cursor-pointer"
                  />

                  <span className="font-bold text-sm sm:text-base">
                    Select to Generate Barcode
                  </span>

                </label>
              )}

              {/* {tab === "print" && (
                <button
                  onClick={() => addToPrint(book)}
                  className="mt-2 sm:mt-3 py-2 sm:py-3 bg-black text-white rounded-xl font-bold flex items-center justify-center gap-2 text-sm sm:text-base"
                >
                  <Printer size={16} /> Add to Print
                </button>
              )} */}

              {tab === "print" && (
                <div className="mt-3 flex flex-col sm:flex-row items-stretch sm:items-center gap-2">

                  {/* Qty input */}
                  <input
                    type="number"
                    min="0"
                    value={printQty[book.id] === undefined ? 0 : printQty[book.id]}
                    onChange={(e) => {
                      const val = e.target.value;
                      setPrintQty(prev => ({
                        ...prev,
                        [book.id]: val === "" ? 0 : parseInt(val)
                      }));
                    }}
                    className="w-full sm:w-20 px-2 py-2 border rounded-lg text-center font-bold"
                  />

                  {/* Add to print */}
                  <button
                    onClick={() => {
                      const qty = parseInt(printQty[book.id]) || 0;
                      addToPrint(book, qty);
                    }}
                    className="w-full sm:flex-1 py-2 bg-indigo-600 text-white rounded-xl font-bold
                 flex items-center justify-center gap-2"
                  >
                    <Printer size={16} /> Add
                  </button>

                </div>
              )}

            </motion.div>
          ))}
        </div>
      </div>

      {/* ======================
          PRINT PREVIEW
      ====================== */}
      {/* {labels.length > 0 && (
        <div className="print-area p-4 sm:p-6 bg-white max-w-6xl mx-auto rounded-3xl shadow-xl mt-6">
         ...same as original...
        </div>
      )} */}

      {labels.length > 0 && (
        <div className="print-area bg-white max-w-[210mm] mx-auto p-2">
          {/* Changed to grid-cols-4 and reduced gap to ensure they fit side-by-side */}
          <div className="grid grid-cols-4 gap-x-2 gap-y-6">
            {renderLabels().map((label) => (
              <div
                key={label.key}
                className="flex flex-col items-center overflow-hidden"
              >
                {/* Width reduced to ~48mm to fit 4 across a 210mm page with gaps */}
                <div className="w-[48mm] flex flex-col">

                  {/* BARCODE IMAGE */}
                  <img
                    src={`${API_BASE_URL}${label.barcodeImagePath}`}
                    alt="barcode"
                    className="w-full h-[15mm] object-fill"
                    loading="eager"
                    onError={(e) => (e.target.style.display = "none")}
                  />

                  {/* TEXT ROW */}
                  <div className="mt-1 flex w-full text-[9px] font-semibold leading-tight px-5">
                    {/* Value stays visible */}
                    <span className="flex-shrink-0 mr-1">
                      {label.barcodeValue}
                    </span>

                    {/* Name truncates with ... */}
                    <span className="truncate">
                      {label.booknameinenglish || label.product}
                    </span>
                  </div>

                  {/* UNDERLINE */}
                  <div className="w-full border-b-[1px] border-black mt-0.5 px-5"></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ======================
          FLOATING ACTION BAR
      ====================== */}
      <div className="fixed bottom-4 sm:bottom-6 left-1/2 -translate-x-1/2 w-[95%] max-w-xl bg-white/95 backdrop-blur-xl p-3 sm:p-4 rounded-2xl shadow-2xl flex flex-col sm:flex-row gap-2 sm:gap-3">
        {tab === "generate" && (
          <button onClick={handleGenerate} disabled={!Object.keys(selected).length} className="flex-1 bg-indigo-600 text-white py-2 sm:py-4 rounded-xl font-black disabled:opacity-50 flex items-center justify-center gap-2 text-sm sm:text-base">
            <PlusSquare size={18} /> Generate
          </button>
        )}
        {labels.length > 0 && (
          <button onClick={handlePrint} className="flex-1 bg-indigo-600 text-white py-2 sm:py-4 rounded-xl font-black flex items-center justify-center gap-2 text-sm sm:text-base">
            <Printer size={18} /> Print ({labels.reduce((s, l) => s + l.qty, 0)})
          </button>
        )}
      </div>

      {/* ======================
          CAMERA PREVIEW (HIDDEN WHEN INACTIVE)
      ====================== */}
      {/* {cameraActive && (
        <video ref={videoRef} className="fixed bottom-24 right-4 w-32 sm:w-40 rounded-xl shadow-xl" autoPlay muted />
      )} */}
    </div>
  );
}





// import React, { useEffect, useMemo, useRef, useState } from "react";
// import { ScanBarcode, Printer, PlusSquare, Search } from "lucide-react";
// import { motion } from "framer-motion";
// import axios from "axios";
// import { BrowserMultiFormatReader } from "@zxing/library";

// const API = "http://localhost:5152";
// const PAGE_SIZE = 20;

// export default function BarcodeManager() {
//   const [books, setBooks] = useState([]);
//   const [tab, setTab] = useState("generate");
//   const [search, setSearch] = useState("");
//   const [page, setPage] = useState(1);
//   const [loading, setLoading] = useState(false);

//   const [selected, setSelected] = useState({});
//   const [labels, setLabels] = useState([]);
//   const [pageSize, setPageSize] = useState("A4");

//   const [cameraActive, setCameraActive] = useState(false);
//   const [scannedCode, setScannedCode] = useState("");

//   const videoRef = useRef(null);
//   const streamRef = useRef(null);
//   const codeReader = useRef(null);

//   // ======================
//   // FETCH BOOKS
//   // ======================
//   const fetchBooks = async () => {
//     setLoading(true);
//     try {
//       const res = await axios.post(`${API}/Home/Getbooks`, {}, {
//         headers: { "Content-Type": "application/json" }
//       });
//       setBooks(res.data || []);
//     } catch {
//       alert("Failed to load books");
//     } finally {
//       setLoading(false);
//     }
//   };

//   useEffect(() => {
//     fetchBooks();
//   }, []);

//   // ======================
//   // FILTER + PAGINATION
//   // ======================
//   const filtered = useMemo(() => {
//     let data = [...books];
//     data = tab === "generate" ? data.filter(b => !b.isBarcodeGenerated) : data.filter(b => b.isBarcodeGenerated);
//     if (search) data = data.filter(b => (b.booknameinenglish || b.product)?.toLowerCase().includes(search.toLowerCase()));
//     return data;
//   }, [books, tab, search]);

//   const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
//   const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

//   // ======================
//   // SELECTION
//   // ======================
//   const toggleSelect = (book) => {
//     setSelected(prev => {
//       const copy = { ...prev };
//       if (copy[book.id]) delete copy[book.id];
//       else copy[book.id] = { ...book, qty: 1 };
//       return copy;
//     });
//   };

//   const clearSelection = () => {
//     if (!Object.keys(selected).length) return;
//     if (window.confirm("Clear all selected books?")) setSelected({});
//   };

//   // ======================
//   // GENERATE BARCODE
//   // ======================
//   const handleGenerate = async () => {
//     const ids = Object.keys(selected).map(id => parseInt(id));
//     if (!ids.length) return alert("Select books first");

//     try {
//       await axios.post(`${API}/barcode/GenerateBarcodeForBook`, ids, {
//         headers: { "Content-Type": "application/json" }
//       });
//       alert("✅ Barcodes generated");
//       setSelected({});
//       setTab("print");
//       fetchBooks();
//     } catch (err) {
//       console.error(err.response?.data);
//       alert("❌ Generation failed");
//     }
//   };

//   // ======================
//   // PRINT QUEUE
//   // ======================
//   const addToPrint = (book) => {
//     setLabels(prev => {
//       const found = prev.find(l => l.id === book.id);
//       if (found) return prev.map(l => l.id === book.id ? { ...l, qty: l.qty + 1 } : l);
//       return [...prev, { ...book, qty: 1 }];
//     });
//   };

//   const updateLabelQty = (id, qty) => {
//     setLabels(prev => prev.map(l => l.id === id ? { ...l, qty: Math.max(1, parseInt(qty) || 1) } : l));
//   };

//   const removeLabel = (id) => {
//     setLabels(prev => prev.filter(l => l.id !== id));
//   };

//   // ======================
//   // PRINT
//   // ======================
//   const handlePrint = () => {
//     window.print();
//   };

//   // ======================
//   // CAMERA SCAN
//   // ======================
//   const startScan = async () => {
//     if (cameraActive) {
//       codeReader.current?.reset();
//       videoRef.current.srcObject?.getTracks().forEach(t => t.stop());
//       setCameraActive(false);
//       return;
//     }

//     codeReader.current = new BrowserMultiFormatReader();
//     try {
//       const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment" } });
//       videoRef.current.srcObject = stream;
//       videoRef.current.play();
//       setCameraActive(true);

//       codeReader.current.decodeFromVideoDevice(
//         null,
//         videoRef.current,
//         (result, err) => {
//           if (result) {
//             const code = result.getText();
//             setScannedCode(code);
//             console.log("Scanned barcode:", code);

//             // Auto-select matching book
//             const book = books.find(b => b.barcodeValue === code);
//             if (book) setSelected(prev => ({ ...prev, [book.id]: { ...book, qty: 1 } }));

//             // Stop scanning after success
//             codeReader.current?.reset();
//             videoRef.current.srcObject?.getTracks().forEach(t => t.stop());
//             setCameraActive(false);
//           }
//         }
//       );
//     } catch {
//       alert("Camera access denied or not available");
//     }
//   };

//   // ======================
//   // RENDER LABELS
//   // ======================
//   const renderLabels = () => {
//     const result = [];
//     labels.forEach(l => {
//       for (let i = 0; i < l.qty; i++) result.push({ ...l, key: `${l.id}-${i}` });
//     });
//     return result;
//   };

//   // ======================
//   // DASHBOARD STATS
//   // ======================
//   const totalBooks = books.length;
//   const barcodeGenerated = books.filter(b => b.isBarcodeGenerated).length;
//   const remainingBarcodes = totalBooks - barcodeGenerated;

//   return (
//     <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-purple-50">
//       {/* Dashboard */}
//       <div className="p-4 max-w-6xl mx-auto flex flex-col sm:flex-row justify-between gap-4 mb-4">
//         <div className="bg-white p-4 rounded-2xl shadow-md text-center flex-1">
//           <p className="font-bold text-gray-500">Total Books</p>
//           <p className="text-2xl font-black">{totalBooks}</p>
//         </div>
//         <div className="bg-white p-4 rounded-2xl shadow-md text-center flex-1">
//           <p className="font-bold text-gray-500">Barcodes Generated</p>
//           <p className="text-2xl font-black">{barcodeGenerated}</p>
//         </div>
//         <div className="bg-white p-4 rounded-2xl shadow-md text-center flex-1">
//           <p className="font-bold text-gray-500">Remaining</p>
//           <p className="text-2xl font-black">{remainingBarcodes}</p>
//         </div>
//       </div>

//       {/* Top bar */}
//       <div className="sticky top-0 z-50 bg-white/95 backdrop-blur-xl shadow-md border-b p-4 space-y-2">
//         <div className="flex flex-row gap-3 items-center">
//           <div className="relative flex-1">
//             <Search className="absolute top-1/2 -translate-y-1/2 left-3 text-gray-400" size={20} />
//             <input
//               placeholder="Search books..."
//               className="w-full pl-10 p-3 sm:p-4 rounded-2xl border-2 focus:border-indigo-500 focus:outline-none text-sm sm:text-base"
//               value={search}
//               onChange={(e) => { setSearch(e.target.value); setPage(1); }}
//             />
//           </div>
//           <motion.button
//             whileTap={{ scale: 0.95 }}
//             onClick={startScan}
//             className={`inline sm:w-auto px-4 sm:px-6 py-3 sm:py-4 rounded-2xl font-bold flex items-center justify-center gap-2 text-sm sm:text-base ${cameraActive ? "bg-red-600 text-white" : "bg-indigo-600 text-white"}`}
//           >
//             <ScanBarcode size={18} /> {cameraActive ? "Stop" : "Scan"}
//           </motion.button>


//         </div>

//         {scannedCode && <p className="text-green-700 font-bold mt-2 text-sm sm:text-base">Scanned: {scannedCode}</p>}

//         <div className="flex flex-row gap-2 mt-2">
//           {["generate","print"].map(t => (
//             <button
//               key={t}
//               onClick={() => { setTab(t); setPage(1); }}
//               className={`flex-1 py-2 sm:py-3 rounded-xl font-bold text-center text-sm sm:text-base transition-colors ${tab===t ? "bg-indigo-600 text-white" : "bg-gray-200"}`}
//             >
//               {t === "generate" ? "Generate" : "Print"}
//             </button>
//           ))}
//         </div>
//       </div>

//       {/* Book cards */}
//       <div className="p-4 max-w-6xl mx-auto">
//         {loading && <p className="text-center py-10 text-gray-500">Loading...</p>}
//         <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
//           {paginated.map(book => (
//             <motion.div
//               key={book.id}
//               whileHover={{ scale: 1.02 }}
//               className="bg-white rounded-2xl shadow-lg p-4 sm:p-5 flex flex-col gap-2 sm:gap-3"
//             >
//               <h3 className="text-base sm:text-lg font-black truncate">{book.booknameinenglish || book.product}</h3>
//               <p className="text-gray-600 text-sm sm:text-base">{book.price ? `₹${book.price}` : "Price N/A"}</p>
//               {book.barcodeValue && (
//                 <div className="mt-1 font-mono text-xs sm:text-sm bg-green-100 text-green-800 px-2 py-1 rounded-full inline-block truncate">{book.barcodeValue}</div>
//               )}

//               {tab === "generate" && (
//                 <button
//                   onClick={() => toggleSelect(book)}
//                   className={`mt-2 py-2 sm:py-3 rounded-xl font-bold flex items-center justify-center gap-2 text-sm sm:text-base ${selected[book.id] ? "bg-indigo-600 text-white" : "bg-indigo-100"}`}
//                 >
//                   <PlusSquare size={18} /> {selected[book.id] ? "Selected" : "Select"}
//                 </button>
//               )}

//               {tab === "print" && (
//                 <button
//                   onClick={() => addToPrint(book)}
//                   className="mt-2 sm:mt-3 py-2 sm:py-3 bg-black text-white rounded-xl font-bold flex items-center justify-center gap-2 text-sm sm:text-base"
//                 >
//                   <Printer size={16} /> Add to Print
//                 </button>
//               )}
//             </motion.div>
//           ))}
//         </div>
//       </div>

//       {/* Print Preview */}
//       {labels.length > 0 && (
//         <div className="print-area p-4 sm:p-6 bg-white max-w-6xl mx-auto rounded-3xl shadow-xl mt-6">
//           <div className="flex flex-col sm:flex-row sm:justify-between items-center mb-4 gap-2 sm:gap-3">
//             <h2 className="text-lg sm:text-xl font-black flex items-center gap-2"><Printer size={20} /> Print Preview</h2>
//             <div className="flex items-center gap-2">
//               <label className="font-bold text-gray-700 text-sm sm:text-base">Page Size:</label>
//               <select className="border rounded-lg p-1 sm:p-2 text-sm sm:text-base" value={pageSize} onChange={e => setPageSize(e.target.value)}>
//                 <option value="A4">A4</option>
//                 <option value="Letter">Letter</option>
//                 <option value="Legal">Legal</option>
//               </select>
//             </div>
//           </div>

//           <div className="overflow-x-auto">
//             <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4 min-w-[250px] sm:min-w-[500px]">
//               {renderLabels().map(l => (
//                 <div key={l.key} className="border p-2 sm:p-4 rounded-xl text-center flex flex-col gap-1">
//                   <img src={`${API}${l.barcodeImagePath}`} alt="Barcode" className="h-16 sm:h-20 mx-auto mb-1" />
//                   <p className="font-mono text-xs sm:text-sm">{l.barcodeValue}</p>
//                 </div>
//               ))}
//             </div>
//           </div>

//           <div className="mt-4 flex justify-end">
//             <button onClick={handlePrint} className="bg-black text-white py-2 sm:py-3 px-4 sm:px-6 rounded-xl font-black flex items-center justify-center gap-2 text-sm sm:text-base">
//               <Printer size={18} /> Print ({labels.reduce((s,l)=>s+l.qty,0)})
//             </button>
//           </div>
//         </div>
//       )}

//       {/* Floating Action Bar */}
//       <div className="fixed bottom-4 sm:bottom-6 left-1/2 -translate-x-1/2 w-[95%] max-w-xl bg-white/95 backdrop-blur-xl p-3 sm:p-4 rounded-2xl shadow-2xl flex flex-col sm:flex-row gap-2 sm:gap-3 z-50">
//         {tab==="generate" && (
//           <button onClick={handleGenerate} disabled={!Object.keys(selected).length} className="flex-1 bg-indigo-600 text-white py-2 sm:py-4 rounded-xl font-black disabled:opacity-50 flex items-center justify-center gap-2 text-sm sm:text-base">
//             <PlusSquare size={18} /> Generate
//           </button>
//         )}
//         {labels.length>0 && (
//           <button onClick={handlePrint} className="flex-1 bg-black text-white py-2 sm:py-4 rounded-xl font-black flex items-center justify-center gap-2 text-sm sm:text-base">
//             <Printer size={18} /> Print ({labels.reduce((s,l)=>s+l.qty,0)})
//           </button>
//         )}
//       </div>

//       {/* Camera Preview */}
//       <video ref={videoRef} className="fixed bottom-24 right-4 w-32 sm:w-40 rounded-xl shadow-xl" />
//     </div>
//   );
// }
